import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { 
  Star, MapPin, Clock, Calendar, CheckCircle, 
  MessageCircle, Shield, Award, Users, Video,
  ChevronRight, Loader2, ThumbsUp, Quote
} from 'lucide-react';
import { TutorTrustBadges } from '../../components/services/EducationBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Rating Stars Component
function RatingStars({ rating, showValue = true }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star 
          key={star}
          className={`w-4 h-4 ${
            star <= rating 
              ? 'fill-amber-400 text-amber-400' 
              : 'text-gray-300'
          }`}
        />
      ))}
      {showValue && <span className="ml-1 font-medium">{rating}</span>}
    </div>
  );
}

// Review Card
function ReviewCard({ review }) {
  return (
    <Card className="bg-gray-50">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
            <span className="font-semibold text-indigo-600">
              {review.student_name?.charAt(0) || 'S'}
            </span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium">{review.student_name}</span>
              {review.verified_purchase && (
                <Badge className="bg-green-100 text-green-700 text-xs">Verified</Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mb-2">
              <RatingStars rating={review.rating} showValue={false} />
              <span className="text-xs text-gray-500">
                {review.subject} • {review.level}
              </span>
            </div>
            <p className="text-sm text-gray-600">{review.comment}</p>
            {review.would_recommend && (
              <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
                <ThumbsUp className="w-3 h-3" />
                Would recommend
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Component
export default function TutorProfilePage() {
  const { tutorId } = useParams();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('about');
  
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API_URL}/api/education-tuition/tutors/${tutorId}/profile`);
        const data = await res.json();
        
        if (data.success) {
          setProfile(data);
        } else {
          setError('Failed to load tutor profile');
        }
      } catch (err) {
        setError('An error occurred while loading the profile');
      } finally {
        setLoading(false);
      }
    };
    
    if (tutorId) fetchProfile();
  }, [tutorId]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const { tutor, verification_status, trust_score, recent_reviews, can_book } = profile;
  const currencySymbol = (tutor.currency === 'LKR' || (tutor.country || '').toUpperCase() === 'LK' || (tutor.country || '').toUpperCase() === 'SL') ? 'Rs.' : '£';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="tutor-profile-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            className="text-white/80 hover:text-white mb-4"
            onClick={() => navigate(-1)}
          >
            ← Back to Tutors
          </Button>
          
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Profile Image */}
            <div className="relative">
              <img 
                src={tutor.profile_image}
                alt={tutor.name}
                className="w-32 h-32 rounded-xl object-cover ring-4 ring-white/20"
              />
              {verification_status.dbs_checked && (
                <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-1.5">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
              )}
            </div>
            
            {/* Basic Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <h1 className="text-2xl md:text-3xl font-bold">{tutor.name}</h1>
                <TutorTrustBadges badges={tutor.badges} maxDisplay={4} size="sm" />
              </div>
              
              <p className="text-indigo-100 mb-3">
                {tutor.subjects?.join(' • ')}
              </p>
              
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                  <span className="font-semibold">{tutor.rating || 'New'}</span>
                  <span className="text-indigo-200">
                    ({tutor.total_reviews || 0} reviews)
                  </span>
                </div>
                
                <div className="flex items-center gap-1 text-indigo-200">
                  <Users className="w-4 h-4" />
                  {tutor.total_sessions || 0} sessions
                </div>
                
                <div className="flex items-center gap-1 text-indigo-200">
                  <Clock className="w-4 h-4" />
                  {tutor.response_time}
                </div>
                
                {tutor.country && (
                  <div className="flex items-center gap-1 text-indigo-200">
                    <MapPin className="w-4 h-4" />
                    {tutor.country}
                  </div>
                )}
              </div>
            </div>
            
            {/* Price & Book */}
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center min-w-[180px]">
              <p className="text-indigo-200 text-sm mb-1">From</p>
              <p className="text-3xl font-bold mb-1">
                {currencySymbol}{tutor.hourly_rate}
              </p>
              <p className="text-indigo-200 text-sm mb-4">per hour</p>
              
              <Button 
                className="w-full bg-white text-indigo-600 hover:bg-indigo-50"
                onClick={() => navigate(`/book/education/${tutorId}`)}
                disabled={!can_book}
                data-testid="book-now-btn"
              >
                Book Now <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              
              {!can_book && (
                <p className="text-xs text-indigo-200 mt-2">
                  Tutor verification pending
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Trust Score Banner */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                <span className="font-medium">Trust Score</span>
                <span className="text-xl font-bold text-green-600">{trust_score}%</span>
              </div>
              
              <div className="hidden md:flex items-center gap-3 text-sm text-gray-500">
                {verification_status.photo_verified && (
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-500" /> Identity Verified
                  </span>
                )}
                {verification_status.dbs_checked && (
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-500" /> DBS Checked
                  </span>
                )}
                {verification_status.qualified_teacher && (
                  <span className="flex items-center gap-1">
                    <Award className="w-4 h-4 text-amber-500" /> Qualified Teacher
                  </span>
                )}
              </div>
            </div>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate(`/tutor/${tutorId}/verification`)}
            >
              View Verification Details
            </Button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Tabs */}
            <div className="flex gap-2 border-b">
              {['about', 'reviews', 'qualifications'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 font-medium capitalize transition-colors ${
                    activeTab === tab
                      ? 'text-indigo-600 border-b-2 border-indigo-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
            
            {/* About Tab */}
            {activeTab === 'about' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>About Me</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 leading-relaxed">
                      {tutor.bio || 'No bio available.'}
                    </p>
                  </CardContent>
                </Card>
                
                {tutor.teaching_style && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Teaching Style</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">{tutor.teaching_style}</p>
                    </CardContent>
                  </Card>
                )}
                
                <Card>
                  <CardHeader>
                    <CardTitle>Subjects & Levels</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">Subjects</h4>
                        <div className="flex flex-wrap gap-2">
                          {tutor.subjects?.map((subject, i) => (
                            <Badge key={i} variant="secondary">{subject}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">Levels</h4>
                        <div className="flex flex-wrap gap-2">
                          {tutor.levels?.map((level, i) => (
                            <Badge key={i} variant="outline">{level}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Reviews Tab */}
            {activeTab === 'reviews' && (
              <div className="space-y-4">
                {recent_reviews?.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-lg">
                        Student Reviews ({tutor.total_reviews})
                      </h3>
                      <RatingStars rating={tutor.rating} />
                    </div>
                    
                    <div className="space-y-4">
                      {recent_reviews.map((review, i) => (
                        <ReviewCard key={i} review={review} />
                      ))}
                    </div>
                    
                    {tutor.total_reviews > 5 && (
                      <Button variant="outline" className="w-full">
                        View All {tutor.total_reviews} Reviews
                      </Button>
                    )}
                  </>
                ) : (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <Quote className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No reviews yet</p>
                      <p className="text-sm text-gray-400">
                        Be the first to book and leave a review!
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
            
            {/* Qualifications Tab */}
            {activeTab === 'qualifications' && (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Qualifications & Experience</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {tutor.experience_years && (
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-indigo-600" />
                        <span>{tutor.experience_years} years of teaching experience</span>
                      </div>
                    )}
                    
                    {tutor.qualifications?.length > 0 ? (
                      <div>
                        <h4 className="font-medium mb-2">Qualifications</h4>
                        <ul className="space-y-2">
                          {tutor.qualifications.map((qual, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <Award className="w-4 h-4 text-amber-500 mt-1" />
                              <span>{qual}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p className="text-gray-500">No qualifications listed</p>
                    )}
                    
                    {tutor.languages?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Languages</h4>
                        <div className="flex gap-2">
                          {tutor.languages.map((lang, i) => (
                            <Badge key={i} variant="outline">{lang}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Verified Documents */}
                {verification_status.verified_documents?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-green-600" />
                        Verified Credentials
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {verification_status.verified_documents.map((doc, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="capitalize">{doc.replace(/_/g, ' ')}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Book Card */}
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Book a Session</CardTitle>
                <CardDescription>
                  Start learning with {tutor.name?.split(' ')[0]}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Hourly rate</span>
                    <span className="font-semibold">
                      {currencySymbol}{tutor.hourly_rate}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Trial session</span>
                    <span className="font-semibold text-green-600">
                      {currencySymbol}{(tutor.hourly_rate * 0.5 * 0.5).toFixed(2)}
                    </span>
                  </div>
                </div>
                
                <div className="border-t pt-4 space-y-2">
                  {tutor.availability?.online && (
                    <div className="flex items-center gap-2 text-sm">
                      <Video className="w-4 h-4 text-indigo-600" />
                      Online sessions available
                    </div>
                  )}
                  {tutor.availability?.in_person && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-indigo-600" />
                      In-person sessions available
                    </div>
                  )}
                </div>
                
                <Button 
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => navigate(`/book/education/${tutorId}?trial=true`)}
                  disabled={!can_book}
                  data-testid="book-trial-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book a Trial
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate(`/messages?tutor=${tutorId}`)}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </CardContent>
            </Card>
            
            {/* Availability Summary */}
            {tutor.availability && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Availability</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                      const dayAvail = tutor.availability[day];
                      if (!dayAvail || !dayAvail.available) return null;
                      
                      return (
                        <div key={day} className="flex justify-between">
                          <span className="capitalize">{day}</span>
                          <span className="text-gray-500">
                            {dayAvail.start || '09:00'} - {dayAvail.end || '18:00'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
